<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
            width: 0,
        }),
        columnHelper.accessor(() => null, {
            id: 'hideItFromTheCustomer',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Cacher au client',
                    width: 0,
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.hideItFromTheCustomer,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'question',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Question',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.question,
                    length: 240,
                }),
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Réponse',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DisplayContentAction,
                                {
                                    title: 'Réponse',
                                    content: props.row.original.answer,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Voir la réponse',
                                            type: 'primary',
                                            icon: FileTextIcon,
                                        }),
                                },
                            ),
                    },
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route('front.projectManagement.faq', {
                                    tenant: tenant.id,
                                    tenantFaq: props.row.original.id,
                                }),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.postFaqs', {
        tenant: tenant.id,
    }),
    data: {
        tenantId: tenant.id,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :href="
                    route('front.projectManagement.faq', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
